import { useEffect, useRef } from "react";
import { journey, subscribeJourney } from "../journey/journeyState";
import { TOTAL_SCREENS } from "../journey/sections";

/* ── FilmRail ─────────────────────────────────────────────
   The journey is a tracking shot; this is the film it runs on.
   A perforated strip along the top edge moves with the track,
   and a SMPTE-style timecode counts the reel position. Both
   are written straight to the DOM from one subscriber — a
   transform and a text node, nothing else. */

const FPS = 24;
const SECONDS_PER_SCREEN = 4;
const REEL_FRAMES = (TOTAL_SCREENS - 1) * SECONDS_PER_SCREEN * FPS;
const HOLE_PERIOD = 22; // px between perforations

const pad = (n) => String(n).padStart(2, "0");

export function timecode(progress) {
  const f = Math.round(Math.min(Math.max(progress, 0), 1) * REEL_FRAMES);
  const frames = f % FPS;
  const totalSec = Math.floor(f / FPS);
  const sec = totalSec % 60;
  const min = Math.floor(totalSec / 60) % 60;
  const hr = Math.floor(totalSec / 3600);
  return `${pad(hr)}:${pad(min)}:${pad(sec)}:${pad(frames)}`;
}

export default function FilmRail() {
  const stripRef = useRef(null);
  const codeRef = useRef(null);

  useEffect(() => {
    const update = () => {
      const tx = journey.horizontal ? journey.trackX : -journey.progress * window.innerHeight * 4;
      const off = ((tx % HOLE_PERIOD) + HOLE_PERIOD) % HOLE_PERIOD - HOLE_PERIOD;
      if (stripRef.current) stripRef.current.style.transform = `translate3d(${off.toFixed(1)}px,0,0)`;
      if (codeRef.current) codeRef.current.textContent = timecode(journey.progress);
    };
    const unsub = subscribeJourney(update);
    update();
    return unsub;
  }, []);

  return (
    <>
      {/* perforations — wider than the viewport so the wrap is never seen */}
      <div className="fixed top-0 left-0 right-0 h-[10px] z-[60] overflow-hidden pointer-events-none" aria-hidden>
        <div
          ref={stripRef}
          className="absolute top-[3px] left-0 h-[4px] w-[calc(100%+44px)] will-change-transform"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg, rgba(236,231,221,0.28) 0 8px, transparent 8px 22px)",
          }}
        />
      </div>

      {/* timecode — sits under the wordmark, reads as the reel counter */}
      <div className="fixed top-[64px] left-6 sm:left-10 z-50 pointer-events-none select-none" aria-hidden>
        <span ref={codeRef} className="font-mono text-[10px] tracking-[0.25em] text-paper-dim tabular-nums">
          00:00:00:00
        </span>
      </div>
    </>
  );
}
